import { Button, Result } from 'antd';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { describeError } from '../api/error-messages';
import { LoadingState } from '../components/states/LoadingState';
import { useAuth } from '../features/auth/useAuth';
import { ROUTES } from './routes';

export type RequireAuthProps = {
  children: ReactNode;
};

/**
 * Route guard. No protected content, shell chrome, or data request happens
 * before this passes (Story G).
 *
 * Three states, not one:
 *  - **no token** → the login page, remembering where the user was headed;
 *  - **token, session still resolving** → a full-page busy state, so a reload
 *    never flashes the login screen at an authenticated user;
 *  - **token, session failed for a non-401 reason** → a retryable error. A
 *    401 does not land here: the API client clears the session, which drops
 *    this back to the first state.
 *
 * The backend still enforces every permission; this is navigation, not
 * security.
 */
export function RequireAuth({ children }: RequireAuthProps) {
  const { isAuthenticated, isLoading, sessionError, user, retrySession, logout } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    /**
     * `pathname + search` — not `pathname` alone. The query string carries
     * `?workspace=` and `?record=`, so dropping it would return the user to
     * the module but not to the record they deep-linked (Story G).
     */
    return (
      <Navigate
        to={ROUTES.login}
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  if (sessionError) {
    return (
      <div className="flex min-h-dvh items-center justify-center p-4">
        <Result
          status="warning"
          title="Could not load your session"
          subTitle={describeError(sessionError)}
          extra={[
            <Button key="retry" type="primary" onClick={retrySession}>
              Try again
            </Button>,
            <Button key="signout" onClick={logout}>
              Sign out
            </Button>,
          ]}
        />
      </div>
    );
  }

  if (isLoading || user === null) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <LoadingState message="Restoring your session…" />
      </div>
    );
  }

  return <>{children}</>;
}
